import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useDispatch, useSelector } from 'react-redux'
import Helmet from 'react-helmet'
import { IconPencil, IconTrash } from '@tabler/icons-react'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { AlertFilters } from '@/views/RealtimeCoaching/Alerts/AlertFilters'
import {
  fetchAgentsByOrg,
  fetchManagersByOrg,
} from '@/reducers/realtime/realtimeCoaching/realtimeCoaching.actions'
import {
  deleteAlertConfiguration,
  editAlertConfiguration,
  loadAlertConfigurations,
  loadPlaybookNamesAndCids,
} from '@/reducers/realtime/realtime.actions'
import { setFilter } from '@/reducers/realtime/realtimeCoaching/realtimeCoaching.redux'
import { BaltoAdminManagerFilter } from '@/views/RealtimeCoaching/components/filters/BaltoAdminManagerFilter'
import { ModalActions } from '@/components/modals/ModalActions'

import { AlertHeader } from './AlertHeader'
import { EditAlertForm } from './EditAlertForm'
import { createAlertTableRows } from './AlertsHelpers'

import '../realtimeCoaching.scss'

const AlertsPage = () => {
  const [showEditModal, setShowEditModal] = useState(false)
  const [editedAlert, setEditedAlert] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deletedAlert, setDeletedAlert] = useState(null)
  const [alertConfigs, setAlertConfigs] = useState([])
  const [isReadOnly, setIsReadOnly] = useState(false)
  const dispatch = useDispatch()

  const organizationUsers = useSelector((state) => state.organizationUsers.users)
  const organizationTags = useSelector((state) => state.organizationTags)
  // TODO: why we have so many alertConfigurations defined?? can we reduce?
  const newAlertConfiguration = useSelector((state) => state.realtimeNewAlertConfiguration)
  const { alertConfigurations, loading } = useSelector(
    (state) => state.realtimeOrganizationAlertConfigurations
  )
  const { filters } = useSelector((state) => state.realtimeCoaching)
  const { organizationid } = useSelector((state) => state.currentUser)
  const { customBranding } = useSelector((state) => state.customBranding)
  const siteTitle = customBranding?.customBrandingDisplayName || 'Balto Cloud'
  const isBaltoAdmin = organizationid === 1
  const filteredAlertConfigs = alertConfigurations.filter(
    ({ users, active, created_at, modified_at, tags }) => {
      if (filters.alertStatus.length) {
        if (!filters.alertStatus.some((status) => status.value === 'active') && active) {
          return false
        }
        if (!filters.alertStatus.some((status) => status.value === 'deactivated') && !active) {
          return false
        }
      }
      if (filters.agents.length || filters.tags.length) {
        const filterAgentIds = filters.agents.map(({ value }) => value)
        const filterTagIds = filters.tags.map(({ value }) => value)
        if (
          !users.some((userId) => filterAgentIds.includes(userId)) &&
          !tags.some((tagId) => filterTagIds.includes(tagId))
        ) {
          return false
        }
      }
      if (filters.nullableDateRange.startDate && filters.nullableDateRange.endDate) {
        const alertCreated = moment(created_at)
        const alertLastModified = moment(modified_at)
        const filterStartDate = moment(filters.nullableDateRange.startDate)
        const filterEndDate = moment(filters.nullableDateRange.endDate)
        if (
          !alertCreated.isBetween(filterStartDate, filterEndDate, 'days', '[]') &&
          !alertLastModified.isBetween(filterStartDate, filterEndDate, 'days', '[]')
        ) {
          return false
        }
      }
      return true
    }
  )

  useEffect(() => {
    const managerId = filters.baltoAdminManager || null
    dispatch(loadAlertConfigurations({ userId: managerId }))
    dispatch(loadPlaybookNamesAndCids())
    dispatch({
      type: 'REFRESH_USERS_ALERTS',
    })
  }, [])

  useEffect(() => {
    setIsReadOnly(isBaltoAdmin ? filters.organization !== organizationid : false)
  }, [alertConfigurations, organizationid, filters.organization])

  const handleAudibleClick = (id) => {
    const [clickedAlert] = alertConfigurations.filter((alertConfig) => alertConfig.id === id)

    dispatch(
      editAlertConfiguration(
        { data: { ...clickedAlert, audible: !clickedAlert.audible } },
        `Audible notifications for ${clickedAlert.name} has been ${
          clickedAlert.audible ? 'disabled' : 'enabled'
        }`
      )
    )
  }

  const updateActiveState = (alertId) => {
    const [clickedAlert] = alertConfigurations.filter((alertConfig) => alertConfig.id === alertId)

    dispatch(
      editAlertConfiguration(
        { data: { ...clickedAlert, active: !clickedAlert.active } },
        `${clickedAlert.name} has been ${clickedAlert.active ? 'deactivated' : 'activated'}`
      )
    )
  }

  useEffect(() => {
    setAlertConfigs(
      createAlertTableRows(
        filteredAlertConfigs.filter((alertConfig) => alertConfig?.all_users === false),
        organizationTags,
        organizationUsers,
        newAlertConfiguration.configPaths,
        isReadOnly,
        handleAudibleClick,
        updateActiveState
      )
    )
  }, [
    alertConfigurations,
    organizationTags,
    organizationUsers,
    newAlertConfiguration.configPaths,
    isReadOnly,
    filters.alertStatus,
    filters.agents,
    filters.nullableDateRange.startDate,
    filters.nullableDateRange.endDate,
    filters.tags,
  ])

  const deleteAlert = ({ id, name }) => {
    dispatch(deleteAlertConfiguration({ alertConfigurationId: id, alertName: name.value }))
  }

  const columns = [
    {
      accessor: 'name',
      label: 'Alert Name',
      isSearchable: true,
      sticky: true,
    },
    { accessor: 'active_for', label: 'Status' },
    { accessor: 'created_at', label: 'Created On' },
    { accessor: 'alerts_for', label: 'Alert On' },
    {
      accessor: 'type',
      label: 'Type',
      format: (value) => value[0].toUpperCase() + value.slice(1),
    },
    { accessor: 'playbooks_for', label: 'Playbooks' },
  ]

  const actions = [
    {
      label: 'Edit',
      fn: (event, alert) => {
        setShowEditModal(true)
        setEditedAlert(alert)
      },
      icon: <IconPencil />,
    },
  ]

  if (!isReadOnly) {
    actions.push({
      label: 'Delete',
      fn: (event, alert) => {
        setShowDeleteModal(true)
        setDeletedAlert(alert)
      },
      icon: <IconTrash />,
    })
  }

  const handleBaltoManagerFiltersSubmit = ({ userId, userOrganizationId }) => {
    dispatch(loadAlertConfigurations({ userId, userOrganizationId }))
    dispatch(fetchAgentsByOrg(userOrganizationId))
    dispatch(fetchManagersByOrg(userOrganizationId))
    dispatch(setFilter({ agents: [] }))
    dispatch(setFilter({ alertStatus: [] }))
    dispatch(setFilter({ tags: [] }))
    dispatch(setFilter({ nullableDateRange: { startDate: null, endDate: null } }))
    dispatch(setFilter({ nullableDateRangeIsValid: true }))
    dispatch(loadPlaybookNamesAndCids({ userOrganizationId }))
  }

  return (
    <>
      <Helmet>
        <title>Real Time Alerts | {siteTitle}</title>
      </Helmet>

      <AlertHeader readOnly={isReadOnly} loading={loading} />

      {isBaltoAdmin && <BaltoAdminManagerFilter onSubmit={handleBaltoManagerFiltersSubmit} />}
      <AlertFilters />

      <BasicModal
        canCloseOnDimmerClick={false}
        show={showEditModal}
        onClose={() => {
          // eslint-disable-next-line no-alert
          if (window.confirm('Are you sure you want to leave without saving?')) {
            setShowEditModal(false)
          }
        }}
        title={`Editing Alert "${editedAlert?.name.value || ''}"`}
        size="small"
      >
        {editedAlert && (
          <EditAlertForm
            editedAlert={editedAlert}
            closeModal={() => setShowEditModal(false)}
            configPaths={newAlertConfiguration.configPaths}
            readOnly={isReadOnly}
          />
        )}
      </BasicModal>

      {deletedAlert && (
        <BasicModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Alert"
          size="tiny"
        >
          <p>
            {`Are you sure you want to delete the "${deletedAlert.name.value}" alert? This action cannot be reversed.`}
          </p>
          <ModalActions
            onClose={() => setShowDeleteModal(false)}
            onSubmit={() => deleteAlert(deletedAlert)}
            isDelete
          />
        </BasicModal>
      )}

      <Card className="no-padding">
        <AdvancedTable
          loading={loading}
          rows={alertConfigs}
          columns={columns}
          actions={actions}
          pagination
          stickyAction
          hoverable={false}
          testid="rtc-alerts-table"
        />
      </Card>
    </>
  )
}

export const Alerts = withLDConsumer()(AlertsPage)
